import { getOrCreateSessionId } from './sessionId';
import { createRequestId } from '@core/recommendations/requestId';
import type { ClientEvent, EventBatchPayload, RecommendationEventType } from './types';

const FLUSH_INTERVAL_MS = 2000;
const MAX_BUFFER_SIZE = 20;
const API_BASE = import.meta.env.VITE_REC_API_BASE_URL || '/api/v1';

let buffer: ClientEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let currentUserId: string | undefined;

function createEventId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

export function setEventSdkUserId(userId: string | null | undefined): void {
  currentUserId = userId ?? undefined;
}

async function postBatch(payload: EventBatchPayload): Promise<void> {
  try {
    await fetch(`${API_BASE}/events/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': createRequestId(),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget — never block UX
  }
}

export function flushEventBuffer(useBeacon = false): void {
  if (buffer.length === 0) return;

  const payload: EventBatchPayload = {
    session_id: getOrCreateSessionId(),
    events: buffer,
  };
  buffer = [];

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (useBeacon && typeof navigator !== 'undefined' && 'sendBeacon' in navigator) {
    const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    navigator.sendBeacon(`${API_BASE}/events/batch`, blob);
    return;
  }

  void postBatch(payload);
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEventBuffer();
  }, FLUSH_INTERVAL_MS);
}

export function trackEvent(
  eventType: RecommendationEventType,
  fields: {
    shop_id: string;
    product_id?: string;
    user_id?: string;
    request_id?: string;
    metadata?: Record<string, unknown>;
    anonymous_id?: string;
    device_id?: string;
    surface?: string;
    block_type?: string;
    position?: number;
    query?: string;
    category_id?: string;
    variant_id?: string;
    quantity?: number;
    price?: number;
    currency?: string;
    source_request_id?: string;
  }
): void {
  const metadata = {
    ...(fields.metadata ?? {}),
    ...(fields.surface ? { surface: fields.surface } : {}),
    ...(fields.block_type ? { block_type: fields.block_type } : {}),
    ...(fields.position !== undefined ? { position: fields.position } : {}),
    ...(fields.query ? { query: fields.query } : {}),
    ...(fields.category_id ? { category_id: fields.category_id } : {}),
    ...(fields.variant_id ? { variant_id: fields.variant_id } : {}),
    ...(fields.quantity !== undefined ? { quantity: fields.quantity } : {}),
    ...(fields.price !== undefined ? { price: fields.price } : {}),
    ...(fields.currency ? { currency: fields.currency } : {}),
    ...(fields.source_request_id
      ? { source_request_id: fields.source_request_id }
      : {}),
  };

  buffer.push({
    event_id: createEventId(),
    event_type: eventType,
    event_version: 1,
    shop_id: fields.shop_id,
    product_id: fields.product_id,
    session_id: getOrCreateSessionId(),
    user_id: fields.user_id ?? currentUserId,
    request_id: fields.request_id,
    timestamp: new Date().toISOString(),
    metadata,
    anonymous_id: fields.anonymous_id,
    device_id: fields.device_id,
    surface: fields.surface,
    block_type: fields.block_type,
    position: fields.position,
    query: fields.query,
    category_id: fields.category_id,
    variant_id: fields.variant_id,
    quantity: fields.quantity,
    price: fields.price,
    currency: fields.currency,
    source_request_id: fields.source_request_id,
  });

  if (buffer.length >= MAX_BUFFER_SIZE) {
    flushEventBuffer();
    return;
  }

  scheduleFlush();
}

export function trackRecommendationImpressions(fields: {
  shop_id: string;
  request_id: string;
  surface: string;
  block_type?: string;
  items: Array<{
    product_id: string;
    score?: number;
    reason?: string;
  }>;
}): void {
  fields.items.forEach((item, position) => {
    trackEvent('rec_impression', {
      shop_id: fields.shop_id,
      product_id: item.product_id,
      request_id: fields.request_id,
      surface: fields.surface,
      block_type: fields.block_type,
      position,
      metadata: {
        ranker_score: item.score,
        reason: item.reason,
      },
    });
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => flushEventBuffer(true));
  window.addEventListener('beforeunload', () => flushEventBuffer(true));
}
